import { useEffect, useEffectEvent, useId, useRef, useState, type RefObject } from "react";
import { Search as SearchIcon, X as CloseIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CONVERSATION_LIST_SEARCH_MAX } from "@multi-chat/shared";
import { normalizeConversationSearch } from "../../../api/conversation-list-query.js";
import { CONVERSATION_SEARCH_DEBOUNCE_MS } from "../conversation-list-state.js";

interface ConversationSearchFieldProps {
  searching: boolean;
  onSearchChange: (search: string | undefined) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Escape closes the collapsible field and keeps the query. */
  onEscape: () => void;
}

/** Server-backed conversation search. The clear button resets it; Escape closes the field. */
export function ConversationSearchField({
  searching,
  onSearchChange,
  inputRef,
  onEscape,
}: ConversationSearchFieldProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const inputId = useId();
  const hintId = useId();
  const [input, setInput] = useState("");
  const commitSearch = useEffectEvent(onSearchChange);
  const skipInitialDebounceRef = useRef(true);
  const skipDebounceRef = useRef(false);
  const uiDir = i18n.language === "he" ? "rtl" : "ltr";
  const inputDir = input.length === 0 ? uiDir : "auto";

  useEffect(() => {
    if (skipInitialDebounceRef.current) {
      skipInitialDebounceRef.current = false;
      return;
    }
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }
    const timeout = window.setTimeout(() => {
      commitSearch(normalizeConversationSearch(input));
    }, CONVERSATION_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [input]);

  function clearSearch(): void {
    skipDebounceRef.current = true;
    setInput("");
    commitSearch(undefined);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== "Escape") return;
    event.preventDefault();
    onEscape();
  }

  return (
    <search className="min-w-0">
      <label
        htmlFor={inputId}
        className="relative mt-2 mb-[3px] flex h-9 items-center gap-2 rounded-[10px] border border-inbox-line bg-inbox-field ps-3 pe-1 transition-[border-color,box-shadow,background-color] duration-150 focus-within:border-primary focus-within:bg-surface focus-within:shadow-[0_0_0_3px_rgb(23_126_103/0.18)]"
      >
        {searching ? (
          <span
            className="size-4 shrink-0 rounded-full border-2 border-inbox-line border-t-inbox-subtle animate-[spin_0.8s_linear_infinite] motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : (
          <SearchIcon size={16} className="shrink-0 text-inbox-subtle" aria-hidden="true" />
        )}
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          className="h-full min-w-0 flex-1 appearance-none border-0 bg-transparent text-[0.84375rem] text-inbox-text shadow-none outline-none placeholder:text-[#93a19d] focus:outline-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          value={input}
          dir={inputDir}
          maxLength={CONVERSATION_LIST_SEARCH_MAX}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          placeholder={t("inbox.search.placeholder")}
          aria-label={t("inbox.search.label")}
          aria-describedby={hintId}
          aria-busy={searching || undefined}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        {input.length > 0 ? (
          <button
            type="button"
            className="grid size-7 shrink-0 place-items-center rounded-full text-inbox-muted transition-colors hover:bg-inbox-hover focus-visible:outline-2 focus-visible:outline-primary"
            aria-label={t("inbox.search.clear")}
            onClick={() => {
              clearSearch();
              requestAnimationFrame(() => inputRef?.current?.focus({ preventScroll: true }));
            }}
          >
            <CloseIcon size={14} aria-hidden="true" />
          </button>
        ) : null}
      </label>
      <span id={hintId} className="sr-only">
        {t("inbox.search.hint")}
      </span>
    </search>
  );
}
